import axios from 'axios'
import { classifiersStore } from '@/store'
import { ErrorUtils } from '@/commons/utils/error'
import { Classifier, Classifiers } from '@/commons/types'
import { ENVIRONMENT } from '@/commons/configuration'
import { ClassifiersUtils } from '@/commons/utils/classifiers'
import { ClassifierGroup } from '@/commons/constants/classifier'
import authenticatedAxios from '@/commons/configuration/authenticated.axios'

function generateClassifierWithTranslationKey (classifier: Classifier): Classifier {
    classifier.translationKey = `${classifier.parentCode}.${classifier.code}`
    return classifier
}

function handlePLaceOfBirthClassifiers (placeOfBirths: Array<Classifier>): Array<Classifier> {
    const sanitizedClassifiers = []
    let estionianClassifier = {}

    for (const placeOfBirth of placeOfBirths) {
        const normalizedClassifier = generateClassifierWithTranslationKey(placeOfBirth)
        normalizedClassifier.label = ClassifiersUtils.sanitizePlaceOfBirth(placeOfBirth.label)
        if (!normalizedClassifier.label) {
            continue
        }

        if (normalizedClassifier.code === 'EESTI / EST') {
            estionianClassifier = normalizedClassifier
            continue
        }

        sanitizedClassifiers.push(normalizedClassifier)
    }

    sanitizedClassifiers.sort((aBirthPlace: Classifier, bBirthPLace: Classifier) => {
        const aBirthPLaceLabel = aBirthPlace.label.toUpperCase()
        const bBirthPLaceLabel = bBirthPLace.label.toUpperCase()

        if (aBirthPLaceLabel > bBirthPLaceLabel) {
            return 1
        } else if (aBirthPLaceLabel < bBirthPLaceLabel) {
            return -1
        } else {
            return 0
        }
    })

    sanitizedClassifiers.unshift(estionianClassifier as Classifier)
    return sanitizedClassifiers
}

function normalizeClassifierGroup (classifiers: Array<Classifier>, classifierGroupName: string): Array<Classifier> {
    if (classifierGroupName === ClassifierGroup.PLACE_OF_BIRTH) {
        return handlePLaceOfBirthClassifiers(classifiers)
    }

    return classifiers.map(classifier => generateClassifierWithTranslationKey(classifier))
}

export class ClassifiersService {
    static async fetchClassifiers (): Promise<void> {
        if (classifiersStore.isValidClassifiers) {
            return
        }

        ClassifiersService.cancelRequests()
        classifiersStore.requestCancelToken = axios.CancelToken.source()

        try {
            const response = await authenticatedAxios.get(ENVIRONMENT.classifiersUrl, {
                cancelToken: classifiersStore.requestCancelToken.token
            })
            const classifiers: Classifiers & Record<string, Array<Classifier>> = response.data
            const sanitizedClassifiers: Record<string, Array<Classifier>> = {}

            for (const classifierGroupName in classifiers) {
                if (!Object.prototype.hasOwnProperty.call(classifiers, classifierGroupName)) {
                    continue
                }
                sanitizedClassifiers[classifierGroupName] = normalizeClassifierGroup(
                    classifiers[classifierGroupName],
                    classifierGroupName
                )
            }
            classifiersStore.classifiers = sanitizedClassifiers as unknown as Classifiers & Record<string, Array<Classifier>>
        } catch (e) {
            ErrorUtils.requestErrorHandler(ErrorUtils.resolveRequestErrorEntity(e))
        }
    }

    static cancelRequests (): void {
        if (classifiersStore.requestCancelToken) {
            classifiersStore.requestCancelToken.cancel()
        }
    }
}
